import { useState } from "react";
import type { GameSummary } from "backend/types";
import { api } from "./api";
import { usePoll } from "./ui";

const STATUS_LABEL: Record<string, string> = {
  lobby: "waiting",
  active: "in progress",
  ended: "ended",
};

export function Lobby({ onOpen }: { onOpen: (id: string) => void }) {
  const { data, error, refresh } = usePoll(() => api.listGames(), 4000, []);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    const n = name.trim();
    if (!n) return;
    setBusy(true);
    try {
      const r = await api.createGame({ name: n });
      onOpen(r.game.id);
    } finally {
      setBusy(false);
    }
  }

  const games: GameSummary[] = data?.games ?? [];

  return (
    <div className="mx-auto w-full max-w-2xl p-5 space-y-6">
      <form onSubmit={create} className="bg-white rounded-2xl border border-black/5 shadow-sm p-4">
        <h2 className="font-display text-lg font-bold mb-2">Host a room</h2>
        <div className="flex gap-2">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Room name, e.g. Spice Lane"
            maxLength={40}
            className="flex-1 rounded-xl bg-parchment-deep/50 px-3 py-2 outline-none focus:ring-2 focus:ring-wa"
          />
          <button
            disabled={busy || !name.trim()}
            className="rounded-xl bg-wa text-white px-5 py-2 font-semibold hover:bg-wa-dark disabled:opacity-50"
          >
            Create
          </button>
        </div>
        <p className="text-xs text-ink/40 mt-2">
          You'll be the host — you run rounds and review chats, but don't play.
        </p>
      </form>

      <div>
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-display text-lg font-bold">Join a room</h2>
          <button onClick={refresh} className="text-xs text-wa hover:underline">refresh</button>
        </div>
        {error && <p className="text-sm text-red-500">{error}</p>}
        <ul className="space-y-2">
          {games.length === 0 && (
            <li className="text-sm text-ink/40 bg-white rounded-xl border border-black/5 p-4 text-center">
              No rooms yet — host one above.
            </li>
          )}
          {games.map((g) => (
            <li key={g.id}>
              <button
                onClick={() => onOpen(g.id)}
                className="w-full text-left bg-white rounded-xl border border-black/5 shadow-sm hover:shadow px-4 py-3 flex items-center justify-between transition"
              >
                <div>
                  <div className="font-semibold">{g.name}</div>
                  <div className="text-xs text-ink/40">{g.playerCount} in room</div>
                </div>
                <span
                  className={
                    "text-[11px] uppercase tracking-wide rounded-full px-2.5 py-1 " +
                    (g.status === "active"
                      ? "bg-wa/15 text-wa"
                      : g.status === "ended"
                        ? "bg-black/5 text-ink/50"
                        : "bg-gold/20 text-amber-700")
                  }
                >
                  {STATUS_LABEL[g.status] ?? g.status}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
